/**
 * 视频号 CDN 上传器（视频 20302 / 封面 20304）
 *
 * 三阶段：applyupload → uploadpart×N → completepart
 * 分片大小 8MB，ETag 用本地 MD5 拼装（服务端 ETag == 分片 MD5 已实测确认）
 * 支持秒传/续传：同 taskid 已传分片服务端保留，不必重传
 *
 * 数据来源：network-logs.json (8/20) + network-logs1.json (8/19)
 */

import crypto from "node:crypto";
import fs from "node:fs";
import type { Http } from "../../utils/http";

const CHUNK_SIZE = 8 * 1024 * 1024; // 8MB

/**
 * 上传超时调优参数
 *
 * 上传对网络远比普通接口敏感：8MB 分片在慢网下几十秒才走完一片，
 * 撞上过短的超时只会触发无意义的重试并把整体时间拖长，
 * 所以三个阶段统一给 3 分钟的宽松上限。每片独立计时，总时长不设上限。
 */
export interface UploadTuning {
    /** 元数据请求（applyupload）超时 */
    metaTimeout?: number;
    /** 单分片上传超时 */
    partTimeout?: number;
    /** 单分片重试次数（PartNumber + Content-MD5 幂等，重试安全） */
    partRetries?: number;
    /** completepart 超时：服务端要落盘拼装整个文件，大视频天然慢 */
    completeTimeout?: number;
}

/** 上传三阶段统一超时：3 分钟 */
const UPLOAD_STAGE_TIMEOUT = 3 * 60 * 1000;

const DEFAULT_TUNING = {
    metaTimeout: UPLOAD_STAGE_TIMEOUT,
    partTimeout: UPLOAD_STAGE_TIMEOUT,
    partRetries: 3,
    completeTimeout: UPLOAD_STAGE_TIMEOUT,
} as const;

export interface UploadOptions {
    filePath: string;
    /** filetype: 20302=视频, 20304=封面 */
    fileType: number;
    uin: number;
    authKey: string;
    http: Http;
    logger?: { info: (msg: string) => void; error: (msg: string, ...args: any[]) => void };
    /** 超时调优，不传则用 DEFAULT_TUNING */
    tuning?: UploadTuning;
}

export interface UploadResult {
    /** completepart 返回的 DownloadURL */
    downloadUrl: string;
    /** 文件 MD5 */
    md5: string;
    fileSize: number;
}

/**
 * 上传单个文件到视频号 CDN
 *
 * @param opts.filePath 本地文件路径
 * @param opts.fileType 20302=视频 / 20304=封面
 * @param opts.uin weixinnum
 * @param opts.authKey Authorization 头
 */
export async function uploadFile(opts: UploadOptions): Promise<UploadResult> {
    const { filePath, fileType, uin, authKey, http, logger, tuning } = opts;

    const stat = fs.statSync(filePath);
    const fileSize = stat.size;
    const fileName = filePath.split(/[\\/]/).pop() || "file";

    const metaTimeout = tuning?.metaTimeout ?? DEFAULT_TUNING.metaTimeout;
    const partTimeout = tuning?.partTimeout ?? DEFAULT_TUNING.partTimeout;
    const partRetries = tuning?.partRetries ?? DEFAULT_TUNING.partRetries;
    const completeTimeout = tuning?.completeTimeout ?? DEFAULT_TUNING.completeTimeout;

    logger?.info(`[shipinhaoPublishVideo] 开始上传: ${fileName} (${fileSize} B, filetype=${fileType})`);
    // logger?.info(
    //     `上传超时策略: 单片 ${partTimeout / 1000}s（重试 ${partRetries} 次）、合并 ${completeTimeout / 1000}s`,
    // );

    // 计算文件 MD5 用于生成确定性 taskid（秒传/续传关键）
    const fileMd5 = await computeFileMd5(filePath);
    const taskId = generateTaskId(fileName, fileSize, fileMd5);

    const baseUrl = "https://finderassistancea.video.qq.com";
    const headers = { Authorization: authKey };

    // 1. applyupload: 申请 UploadID
    const xArgs = `apptype=251&filetype=${fileType}&weixinnum=${uin}&filekey=${encodeURIComponent(fileName)}&filesize=${fileSize}&taskid=${taskId}&scene=2`;

    const chunkCount = Math.ceil(fileSize / CHUNK_SIZE);
    const blockPartLength: number[] = [];
    for (let i = 0; i < chunkCount; i++) {
        blockPartLength.push(Math.min((i + 1) * CHUNK_SIZE, fileSize));
    }

    logger?.info(`[shipinhaoPublishVideo] 视频分片: ${chunkCount} 片`);
    const applyRes = await http.api<{ UploadID: string; ListPartsResult?: any }>(
        {
            method: "PUT",
            url: `${baseUrl}/applyuploaddfs`,
            headers: { ...headers, "X-Arguments": xArgs },
            data: { BlockSum: chunkCount, BlockPartLength: blockPartLength },
        },
        { timeout: metaTimeout, retries: 2, retryDelay: 2000 },
    );

    if (!applyRes.UploadID && !applyRes.ListPartsResult) {
        throw new Error("申请 UploadID 失败: " + JSON.stringify(applyRes));
    }

    let uploadId = applyRes.UploadID;

    // 续传检测：如果返回 ListPartsResult，说明分片已部分上传
    const uploadedParts = new Set<number>();
    if (applyRes.ListPartsResult) {
        // logger?.info("检测到已上传分片，执行续传");
        const parts = Array.isArray(applyRes.ListPartsResult.Part)
            ? applyRes.ListPartsResult.Part
            : applyRes.ListPartsResult.Part
              ? [applyRes.ListPartsResult.Part]
              : [];
        for (const p of parts) {
            uploadedParts.add(p.PartNumber);
        }
        // 需要再调一次 applyupload 拿 UploadID
        const retryRes = await http.api<{ UploadID: string }>(
            {
                method: "PUT",
                url: `${baseUrl}/applyuploaddfs`,
                headers: { ...headers, "X-Arguments": xArgs },
                data: { BlockSum: chunkCount, BlockPartLength: blockPartLength },
            },
            { timeout: metaTimeout, retries: 2, retryDelay: 2000 },
        );
        uploadId = retryRes.UploadID;
        if (!uploadId) throw new Error("续传获取 UploadID 失败");
    }

    // 2. uploadpart: 上传分片（跳过已传）
    const partInfo: Array<{ PartNumber: number; ETag: string }> = [];
    const fd = fs.openSync(filePath, "r");

    try {
        for (let i = 0; i < chunkCount; i++) {
            const partNumber = i + 1;

            // 秒传/续传：从 ListPartsResult 拿 ETag
            if (uploadedParts.has(partNumber)) {
                const existing = applyRes.ListPartsResult.Part.find((p: any) => p.PartNumber === partNumber);
                if (existing) {
                    partInfo.push({ PartNumber: partNumber, ETag: existing.ETag });
                    // logger?.info(`分片 ${partNumber}/${chunkCount} 已存在，跳过`);
                    continue;
                }
            }

            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, fileSize);
            const chunkSize = end - start;
            const chunk = Buffer.alloc(chunkSize);
            fs.readSync(fd, chunk, 0, chunkSize, start);

            const chunkMd5 = crypto.createHash("md5").update(chunk).digest("hex");

            // uploadpart: scene 改为 0（实测值）
            const partStart = Date.now();
            await http.api(
                {
                    method: "PUT",
                    url: `${baseUrl}/uploadpartdfs?PartNumber=${partNumber}&UploadID=${encodeURIComponent(uploadId)}`,
                    headers: {
                        ...headers,
                        "X-Arguments": xArgs.replace(/scene=2/, "scene=0"),
                        "Content-MD5": chunkMd5,
                    },
                    data: chunk,
                },
                { timeout: partTimeout, retries: partRetries, retryDelay: 2000 },
            );
            const partElapsed = Date.now() - partStart;
            const throughput = Math.round(chunkSize / (partElapsed / 1000));

            // ETag = 分片 MD5（带引号，服务端格式）
            partInfo.push({ PartNumber: partNumber, ETag: `"${chunkMd5}"` });
            // logger?.info(
            //     `分片 ${partNumber}/${chunkCount} 完成 (${chunkSize} B, ${partElapsed}ms, ${Math.round(throughput / 1024)}KB/s)`,
            // );
        }
    } finally {
        fs.closeSync(fd);
    }

    // 3. completepart: 合并
    // 不重试：服务端要落盘拼装整个文件，失败重发代价高且拿不到更多信息，给足单次时间更划算
    // logger?.info("合并分片...");
    const completeRes = await http.api<{ DownloadURL: string }>(
        {
            method: "POST",
            url: `${baseUrl}/completepartuploaddfs?UploadID=${encodeURIComponent(uploadId)}`,
            headers: { ...headers, "X-Arguments": xArgs },
            data: { TransFlag: "0_0", PartInfo: partInfo },
        },
        { timeout: completeTimeout, retries: 1, retryDelay: 3000 },
    );

    if (!completeRes.DownloadURL) {
        throw new Error("合并分片失败: " + JSON.stringify(completeRes));
    }

    logger?.info(`[shipinhaoPublishVideo] ${fileName} 上传成功`);
    return { downloadUrl: completeRes.DownloadURL, md5: fileMd5, fileSize };
}

async function computeFileMd5(filePath: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const hash = crypto.createHash("md5");
        const stream = fs.createReadStream(filePath);
        stream.on("data", (chunk) => hash.update(chunk));
        stream.on("end", () => resolve(hash.digest("hex")));
        stream.on("error", reject);
    });
}

/** 生成确定性 taskid（基于文件特征，支持秒传） */
function generateTaskId(fileName: string, fileSize: number, fileMd5: string): string {
    const input = `${fileName}-${fileSize}-${fileMd5}`;
    return crypto.createHash("md5").update(input).digest("hex").slice(0, 32);
}
